import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FiDownload, FiPrinter } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { useAuth } from '../../context/AuthContext';
import useCurrency from '../../hooks/useCurrency';
import PageLayout from '../../components/PageLayout';
import {
  FiltersCard, FilterGrid, FilterItem, FilterLabel, FilterSelect,
  KPIGrid, KPI, KL, KV, ToolbarRow, ExportBtn, ReportCard, RTable, Loading, Empty,
} from './reportStyles';
import { loadAssetReportData, exportRowsToExcel, printAssetReport, computeFixedAssetMovement } from '../../utils/assetReportHelper';
import { hasAppreciation } from '../../utils/depreciationHelper';

const AssetMovement = () => {
  const { activeCompany, activeBranch, activeFinancialYear } = useAuth();
  const { formatCurrency } = useCurrency();
  const [data, setData] = useState({ assets: [], depRuns: [] });
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('fy'); // 'fy' | 'all'

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const d = await loadAssetReportData(activeCompany.id, activeBranch);
      setData(d);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [activeCompany, activeBranch]);
  useEffect(() => { load(); }, [load]);

  const fyStart = scope === 'fy' ? (activeFinancialYear?.startDate || null) : null;
  const fyEnd = scope === 'fy' ? (activeFinancialYear?.endDate || null) : null;

  const { rows, totals } = useMemo(
    () => computeFixedAssetMovement(data.assets, data.depRuns, fyStart, fyEnd),
    [data, fyStart, fyEnd]
  );

  // Asset Appreciation column only for companies that have any.
  const showAppr = useMemo(() => data.assets.some(hasAppreciation) || !!totals.appreciation, [data, totals]);

  const columns = [
    { key: 'Category', label: 'Category' },
    { key: 'Opening', label: 'Opening NBV', r: true },
    { key: 'Additions', label: 'Additions', r: true },
    ...(showAppr ? [{ key: 'Appreciation', label: 'Appreciation', r: true }] : []),
    { key: 'Depreciation', label: 'Depreciation', r: true },
    { key: 'Disposals', label: 'Disposals (NBV)', r: true },
    { key: 'Closing', label: 'Closing NBV', r: true },
  ];
  const buildRows = () => rows.map(c => ({
    Category: c.categoryName, Opening: formatCurrency(c.openingNBV), Additions: formatCurrency(c.additions),
    ...(showAppr ? { Appreciation: formatCurrency(c.appreciation) } : {}),
    Depreciation: formatCurrency(c.depreciation), Disposals: formatCurrency(c.disposalNBV), Closing: formatCurrency(c.closingNBV),
  }));
  const scopeLabel = scope === 'fy' ? `FY ${fyStart || ''} → ${fyEnd || ''}` : 'All time';
  const formula = showAppr
    ? 'Opening + Additions + Appreciation − Depreciation − Disposals = Closing'
    : 'Opening + Additions − Depreciation − Disposals = Closing';

  const onPrint = () => printAssetReport({
    company: activeCompany, branch: activeBranch && activeBranch.id !== 'all' ? activeBranch : null,
    title: 'Fixed Asset Movement', filters: scopeLabel, columns, rows: buildRows(),
    totals: { Opening: formatCurrency(totals.openingNBV), Additions: formatCurrency(totals.additions), ...(showAppr ? { Appreciation: formatCurrency(totals.appreciation) } : {}), Depreciation: formatCurrency(totals.depreciation), Disposals: formatCurrency(totals.disposalNBV), Closing: formatCurrency(totals.closingNBV) },
    kpis: [
      { label: 'Additions', value: formatCurrency(totals.additions) },
      ...(showAppr ? [{ label: 'Appreciation', value: formatCurrency(totals.appreciation) }] : []),
      { label: 'Depreciation', value: formatCurrency(totals.depreciation) },
      { label: 'Closing NBV', value: formatCurrency(totals.closingNBV), highlight: true },
    ],
    footerNote: showAppr
      ? 'Opening NBV + Additions + Appreciation − Depreciation − Disposals = Closing NBV'
      : 'Opening NBV + Additions − Depreciation − Disposals = Closing NBV',
  });

  return (
    <PageLayout pageTitle="Asset Movement" breadcrumb="Reports / Fixed Assets / Movement">
      <FiltersCard>
        <FilterGrid>
          <FilterItem>
            <FilterLabel>Period</FilterLabel>
            <FilterSelect value={scope} onChange={(e) => setScope(e.target.value)}>
              <option value="fy">Active Financial Year</option>
              <option value="all">All time</option>
            </FilterSelect>
          </FilterItem>
        </FilterGrid>
      </FiltersCard>

      <KPIGrid>
        <KPI><KL>Additions</KL><KV>{formatCurrency(totals.additions)}</KV></KPI>
        {showAppr && <KPI><KL>Appreciation</KL><KV>{formatCurrency(totals.appreciation)}</KV></KPI>}
        <KPI><KL>Depreciation</KL><KV>{formatCurrency(totals.depreciation)}</KV></KPI>
        <KPI><KL>Disposals (NBV)</KL><KV>{formatCurrency(totals.disposalNBV)}</KV></KPI>
        <KPI $highlight><KL>Closing NBV</KL><KV>{formatCurrency(totals.closingNBV)}</KV></KPI>
      </KPIGrid>

      <ToolbarRow>
        <h3 style={{ color: colors.text.secondary }}>{scopeLabel} · {formula}</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <ExportBtn onClick={() => exportRowsToExcel('AssetMovement', 'Movement', buildRows())}><FiDownload /> Excel</ExportBtn>
          <ExportBtn onClick={onPrint}><FiPrinter /> Print</ExportBtn>
        </div>
      </ToolbarRow>

      {loading ? <Loading>Loading...</Loading> : rows.length === 0 ? <Empty>No asset movement for this period.</Empty> : (
        <ReportCard>
          <RTable>
            <thead>
              <tr>
                <th>Category</th><th className="r">Opening NBV</th><th className="r">Additions</th>
                {showAppr && <th className="r">Appreciation</th>}
                <th className="r">Depreciation</th><th className="r">Disposals (NBV)</th><th className="r">Closing NBV</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.categoryId || 'uncat'}>
                  <td>{c.categoryName}</td>
                  <td className="r">{formatCurrency(c.openingNBV)}</td>
                  <td className="r">{formatCurrency(c.additions)}</td>
                  {showAppr && <td className="r">{formatCurrency(c.appreciation)}</td>}
                  <td className="r">{formatCurrency(c.depreciation)}</td>
                  <td className="r">{formatCurrency(c.disposalNBV)}</td>
                  <td className="r">{formatCurrency(c.closingNBV)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="r">{formatCurrency(totals.openingNBV)}</td>
                <td className="r">{formatCurrency(totals.additions)}</td>
                {showAppr && <td className="r">{formatCurrency(totals.appreciation)}</td>}
                <td className="r">{formatCurrency(totals.depreciation)}</td>
                <td className="r">{formatCurrency(totals.disposalNBV)}</td>
                <td className="r">{formatCurrency(totals.closingNBV)}</td>
              </tr>
            </tfoot>
          </RTable>
        </ReportCard>
      )}
    </PageLayout>
  );
};

export default AssetMovement;
